import React, { useMemo, useState } from 'react';
import { 
  Typography, Box, Slider, Button,
  useMediaQuery, useTheme, Collapse, IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';

const ProductFilters = ({ filters, onFilterChange, filteredProducts, allProducts }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [localPriceRange, setLocalPriceRange] = useState([
    filters.minPrice || 0,
    filters.maxPrice || 0
  ]);

  const { collections, brands, priceRange } = useMemo(() => {
    const collectionsMap = new Map();
    const brandsMap = new Map();
    const prices = allProducts.map(p => parseFloat(p.price) || 0.0);

    filteredProducts.forEach(product => {
      if (product.category) {
        product.category.forEach(collection => {
          collectionsMap.set(collection, (collectionsMap.get(collection) || 0) + 1);
        });
      }
      if (product.brand) {
        brandsMap.set(product.brand, (brandsMap.get(product.brand) || 0) + 1);
      }
    });

    return {
      collections: Array.from(collectionsMap, ([name, count]) => ({ name, count })),
      brands: Array.from(brandsMap, ([name, count]) => ({ name, count })),
      priceRange: {
        min: Math.min(...prices),
        max: Math.max(...prices)
      }
    };
  }, [filteredProducts, allProducts]);

  // Update local price range when filters change externally
  useMemo(() => {
    setLocalPriceRange([
      filters.minPrice || priceRange.min, 
      filters.maxPrice || priceRange.max
    ]);
  }, [filters.minPrice, filters.maxPrice, priceRange.min, priceRange.max]);

  const handleCollectionChange = (collection) => {
    const newCollections = filters.collections[0] === collection ? [] : [collection];
    onFilterChange({ ...filters, collections: newCollections }, 'collection');
  };

  const handleBrandChange = (brand) => {
    const newBrands = filters.brands[0] === brand ? [] : [brand];
    onFilterChange({ ...filters, brands: newBrands }, 'brand');
  };

  // This handler updates local state but doesn't trigger filtering
  const handlePriceChange = (event, newValue) => {
    setLocalPriceRange(newValue);
  };

  // This handler triggers the actual filter when slider is released
  const handlePriceChangeCommitted = (event, newValue) => {
    onFilterChange({ ...filters, minPrice: newValue[0], maxPrice: newValue[1] }, 'price');
  };

  const handleResetPrice = () => {
    const newRange = [priceRange.min, priceRange.max];
    setLocalPriceRange(newRange);
    onFilterChange({ ...filters, minPrice: priceRange.min, maxPrice: priceRange.max }, 'price');
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const scrollbarStyles = {
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.background.paper,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0,0,0,.2)',
      borderRadius: '4px',
    },
  };

  const FilterItem = ({ name, count, isSelected, onClick }) => (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.5,
        px: 1,
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Typography variant="body2">{name}</Typography>
      {isSelected ? (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClick(); }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {count}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {/* {!isMobile && <Typography variant="h6">Filters</Typography>} */}
        {isMobile && (
          <Button
            onClick={toggleExpand}
            variant="outlined"
            fullWidth
            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            Filters
          </Button>
        )}
      </Box>
      
      <Collapse in={isExpanded}>
        <Typography variant="subtitle1" gutterBottom>Collections</Typography>
        <Box sx={{ maxHeight: 320, overflowY: 'auto', mb: 2, ...scrollbarStyles }}>
          {(filters.collections.length > 0 ? collections.filter(c => c.name === filters.collections[0]) : collections).map(({ name, count }) => (
            <FilterItem
              key={name}
              name={name}
              count={count}
              isSelected={filters.collections.includes(name)}
              onClick={() => handleCollectionChange(name)}
            />
          ))}
        </Box>

        <Typography variant="subtitle1" gutterBottom>Brands</Typography>
        <Box sx={{ maxHeight: 320, overflowY: 'auto', mb: 2, ...scrollbarStyles }}>
          {(filters.brands.length > 0 ? brands.filter(b => b.name === filters.brands[0]) : brands).map(({ name, count }) => (
            <FilterItem
              key={name}
              name={name}
              count={count}
              isSelected={filters.brands.includes(name)}
              onClick={() => handleBrandChange(name)}
            />
          ))}
        </Box>

        <Typography variant="subtitle1" gutterBottom>Price Range</Typography>
        <Slider
          value={localPriceRange}
          onChange={handlePriceChange}
          onChangeCommitted={handlePriceChangeCommitted}
          valueLabelDisplay="auto"
          min={priceRange.min}
          max={priceRange.max}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="body2">${localPriceRange[0].toFixed(2)}</Typography>
          <Button size="small" onClick={handleResetPrice}>Reset</Button>
          <Typography variant="body2">${localPriceRange[1].toFixed(2)}</Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ProductFilters;